import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import tw from "twin.macro";

import {
	SectionHeading,
	Subheading as SubheadingBase,
} from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";

import { ReactComponent as SvgDecoratorBlob3 } from "images/svg-decorator-blob-3.svg";

// Main Container with background and text styling
const Container = tw.div`relative bg-gray-900 text-white min-h-screen px-8 py-12`; // Added padding for better vertical spacing

const ThreeColumnContainer = styled.div`
	${tw`flex flex-col items-center md:items-stretch md:flex-row flex-wrap md:justify-center max-w-screen-xl mx-auto py-16 `}
`;

const CardContainer = styled.div`
	${tw`flex flex-col   md:flex-row flex-wrap md:justify-center max-w-screen-xl  gap-8 pt-8`}
`;

const Subheading = tw(SubheadingBase)`mb-4 text-blue-600 text-center`; // Adjusted styling for Subheading
const Heading = tw(
	SectionHeading
)`w-full text-white text-4xl md:text-5xl font-extrabold text-center`; // Centered Heading, larger and bold for emphasis
const Description = tw(
	SectionDescription
)`w-full text-center text-gray-400 max-w-2xl mx-auto`; // Centered Description with max-width for better layout

const VerticalSpacer = tw.div`mt-10 w-full`;

const Column = styled.div`
	${tw`md:w-1/2 lg:w-1/3 max-w-sm`}// Removed bottom margin, added flexible width
`;

const Card = styled.div`
	${tw`flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left bg-gray-800 rounded-lg shadow-lg p-6 transition-all duration-300 ease-in-out`}

	${tw`hover:bg-gray-700 hover:shadow-xl`} // Added subtle hover shadow

	// Ensures minimum height consistency across cards
	${tw`min-h-[200px]`} 

  .textContainer {
		${tw`sm:ml-4 mt-4 sm:mt-0`}
	}

	.title {
		${tw`text-xl font-bold text-blue-400`}// Lighter blue accent for card titles for contrast
	}

	.description {
		${tw`mt-2 text-gray-300 leading-relaxed`}// Slightly lighter gray for readable description, adjusted line height
	}
`;

const DecoratorBlob = styled(SvgDecoratorBlob3)`
	${tw`pointer-events-none absolute right-0 bottom-0 w-64 opacity-25 transform translate-x-32 translate-y-48`}
`;

export default () => {
	const { t } = useTranslation();

	// Memoize the card content
	const memoizedCards = useMemo(() => {
		return t("helpSection.cards", { returnObjects: true });
	}, [t]);

	return (
		<Container>
			<ThreeColumnContainer>
				<Subheading>{t("helpSection.subheading")}</Subheading>
				<Heading>{t("helpSection.title")}</Heading>
				<Description>{t("helpSection.description")}</Description>{" "}
				{/* Applied the same styling */}
				<VerticalSpacer />
				<CardContainer>
					{memoizedCards.map((card, i) => (
						<Column key={i}>
							<Card>
								<div className="textContainer">
									<h2 className="title">{card.title}</h2>
									<p className="description">{card.description}</p>
								</div>
							</Card>
						</Column>
					))}
				</CardContainer>
			</ThreeColumnContainer>
		</Container>
	);
};
